import React, { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ArrowsDownUpIcon,
} from '@phosphor-icons/react';
import { CoreEnumOption } from 'src/types/application/inventory-management/ProductV2DTO';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface AttributeOptionsListProps {
  options: CoreEnumOption[];
  defaultValue?: unknown;
  onToggleOption?: (optionId: string, enabled: boolean) => void;
}

type SortField = 'name' | 'description' | 'sortOrder' | 'originSystem';
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 10;

export function AttributeOptionsList({
  options,
  defaultValue,
  onToggleOption,
}: AttributeOptionsListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Handle sort toggle
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort options
  const filteredAndSortedOptions = useMemo(() => {
    // First filter
    let filtered = options;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = options.filter((option) => {
        const matchesName = option.name?.toLowerCase().includes(query);
        const matchesDescription = option.description
          ?.toLowerCase()
          .includes(query);
        const matchesId = option.id.toLowerCase().includes(query);
        const matchesOriginId = option.originId?.toLowerCase().includes(query);

        return (
          matchesName || matchesDescription || matchesId || matchesOriginId
        );
      });
    }

    // Then sort
    return [...filtered].sort((a, b) => {
      let compareValue = 0;

      switch (sortField) {
        case 'name':
          compareValue = (a.name || '').localeCompare(b.name || '');
          break;
        case 'description':
          compareValue = (a.description || '').localeCompare(
            b.description || '',
          );
          break;
        case 'sortOrder':
          compareValue = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
          break;
        case 'originSystem':
          compareValue = (a.originSystem || '').localeCompare(
            b.originSystem || '',
          );
          break;
      }

      return sortDirection === 'asc' ? compareValue : -compareValue;
    });
  }, [options, searchQuery, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(
    filteredAndSortedOptions.length / ITEMS_PER_PAGE,
  );
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedOptions = filteredAndSortedOptions.slice(startIndex, endIndex);

  // Reset to page 1 when search or sort changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortField, sortDirection]);

  return (
    <div className="space-y-3">
      {/* Search Bar */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search options by name, description, or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Showing {paginatedOptions.length > 0 ? startIndex + 1 : 0} to{' '}
          {Math.min(endIndex, filteredAndSortedOptions.length)} of{' '}
          {filteredAndSortedOptions.length}
          {searchQuery && ` (filtered from ${options.length} total)`}
        </span>
        {filteredAndSortedOptions.length > ITEMS_PER_PAGE && (
          <span>
            Page {currentPage} of {totalPages}
          </span>
        )}
      </div>

      {/* Options Table */}
      {paginatedOptions.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">
            {searchQuery
              ? 'No options match your search'
              : 'No options available'}
          </p>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Active</TableHead>
                <TableHead className="w-[30%]">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-1 hover:text-foreground"
                  >
                    Name
                    <ArrowsDownUpIcon className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead className="w-[35%]">
                  <button
                    onClick={() => handleSort('description')}
                    className="flex items-center gap-1 hover:text-foreground"
                  >
                    Description
                    <ArrowsDownUpIcon className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead className="w-16 text-center">
                  <button
                    onClick={() => handleSort('sortOrder')}
                    className="flex items-center gap-1 hover:text-foreground mx-auto"
                  >
                    Sort
                    <ArrowsDownUpIcon className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead className="w-[20%]">
                  <button
                    onClick={() => handleSort('originSystem')}
                    className="flex items-center gap-1 hover:text-foreground"
                  >
                    Origin
                    <ArrowsDownUpIcon className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead className="w-20">Metadata</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedOptions.map((option) => (
                <TableRow key={option.id} className="group">
                  <TableCell>
                    <Switch
                      checked={true}
                      onCheckedChange={(checked) => {
                        if (onToggleOption) {
                          onToggleOption(option.id, checked);
                        }
                      }}
                      disabled={!onToggleOption}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    {option.name || (
                      <span className="text-muted-foreground italic">
                        Unnamed
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {option.description ? (
                      <span className="text-sm text-muted-foreground line-clamp-2">
                        {option.description}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">
                        No description
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-sm font-mono">
                    {option.sortOrder ?? '-'}
                  </TableCell>
                  <TableCell>
                    {option.originSystem || option.originId ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {option.originSystem && (
                          <Badge variant="secondary" className="text-xs">
                            {option.originSystem}
                          </Badge>
                        )}
                        {option.originId && (
                          <code className="text-xs text-muted-foreground truncate">
                            {option.originId}
                          </code>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {option.id === defaultValue && (
                        <Badge variant="outline" className="text-xs">
                          Default
                        </Badge>
                      )}
                      {option.originMetadata && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <button className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted">
                              meta
                            </button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl max-h-[80vh]">
                            <DialogHeader>
                              <DialogTitle>Origin Metadata</DialogTitle>
                            </DialogHeader>
                            <div className="overflow-auto">
                              <pre className="text-xs whitespace-pre-wrap p-3 bg-muted rounded-md">
                                {JSON.stringify(option.originMetadata, null, 2)}
                              </pre>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination Controls */}
      {filteredAndSortedOptions.length > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-between pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <CaretLeftIcon className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
            <CaretRightIcon className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
